import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { createShapeCanvas } from './shapes';
import { recolorGradient } from './recolor';
import lotusGradient from '@shared/assets/falling-petals/lotus-gradient.png';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

// 蓮花用成熟美術素材（B 素材，上色後的 canvas）；菩提葉維持程式繪製的 canvas 圖形
async function createPetalCanvas(type, color) {
    if (type === 'bodhi') return createShapeCanvas(type, 128, color);
    return recolorGradient(lotusGradient, color);
}

/**
 * 飄落蓮花 / 菩提葉特效 - Three.js 3D 實作
 * Falling lotus flowers / bodhi leaves - Three.js 3D implementation
 *
 * 蓮花使用成熟美術素材（B 素材，側視漸層彩色，取自 freesvg.org CC0），
 * 菩提葉維持程式繪製。在 3D 空間中飄落，帶有透視深度與光影。
 *
 * 蓮花本體是平面貼圖，若只用單一平面繞 Y 軸旋轉，轉到側面時會變成一條線消失、很突兀
 * （菩提葉本身就是薄片狀，這樣轉沒問題，但蓮花是立體花朵不該這樣）。因此蓮花改用兩張
 * 貼圖十字交叉（互相垂直 90 度）的經典「cross-billboard」技法：無論轉到哪個角度，
 * 至少有一張貼圖接近正面朝向鏡頭，旋轉時不會穿幫消失，呈現螺旋下降的樣子。
 * 菩提葉維持單一平面、三軸隨機翻轉，像真實落葉一樣打轉飄落。
 */
const FallingPetalsThree = ({
    type = 'lotus',
    count = 60,
    color = '#F5C97B',
    minSize = 0.5,
    maxSize = 1.1,
    wind = 0.15,
    speed = 1,
    className = '',
}) => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;
        const horizontalOnly = type === 'lotus';
        let cancelled = false;
        let cleanup = () => {};

        createPetalCanvas(type, color).then((petalCanvas) => {
            if (cancelled) return;

            const width = mount.clientWidth;
            const height = mount.clientHeight;

            const scene = new THREE.Scene();
            const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
            camera.position.set(0, 0, 12);

            const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
            renderer.setSize(width, height);
            mount.appendChild(renderer.domElement);

            const texture = new THREE.CanvasTexture(petalCanvas);
            texture.needsUpdate = true;
            // 用 MeshBasicMaterial（不受光照影響）而非 MeshStandardMaterial，
            // 確保貼圖顯示的顏色就是 recolor.js 算出來的原始色票，不會被光源顏色/強度洗色
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                side: THREE.DoubleSide,
            });

            const worldWidth = 14;
            const worldHeight = 10;
            const depthRange = 6;

            const geometryPool = [];

            // 蓮花：兩張貼圖十字交叉組成一個 Group，避免旋轉到側面時穿幫消失
            const createLotusObject = (size) => {
                const geometry = new THREE.PlaneGeometry(size, size);
                geometryPool.push(geometry);
                const planeA = new THREE.Mesh(geometry, material);
                const planeB = new THREE.Mesh(geometry, material);
                planeB.rotation.y = Math.PI / 2;
                const group = new THREE.Group();
                group.add(planeA, planeB);
                return group;
            };

            // 菩提葉：單一平面，三軸翻轉本來就符合薄片狀落葉的樣子
            const createBodhiObject = (size) => {
                const geometry = new THREE.PlaneGeometry(size, size);
                geometryPool.push(geometry);
                return new THREE.Mesh(geometry, material);
            };

            const petals = Array.from({ length: count }, () => {
                const size = randomBetween(minSize, maxSize);
                const obj = horizontalOnly ? createLotusObject(size) : createBodhiObject(size);
                obj.position.set(
                    randomBetween(-worldWidth / 2, worldWidth / 2),
                    randomBetween(-worldHeight / 2, worldHeight / 2),
                    randomBetween(-depthRange / 2, depthRange / 2)
                );
                if (horizontalOnly) {
                    obj.rotation.y = randomBetween(0, Math.PI * 2);
                } else {
                    obj.rotation.set(
                        randomBetween(0, Math.PI * 2),
                        randomBetween(0, Math.PI * 2),
                        randomBetween(0, Math.PI * 2)
                    );
                }
                scene.add(obj);
                return {
                    obj,
                    fallSpeed: randomBetween(0.01, 0.03) * speed,
                    swaySpeed: randomBetween(0.5, 1.3),
                    swayAmplitude: randomBetween(0.4, 1.2),
                    swayOffset: randomBetween(0, Math.PI * 2),
                    rotSpeedX: horizontalOnly ? 0 : randomBetween(-0.015, 0.015),
                    rotSpeedY: randomBetween(0.015, 0.03) * (Math.random() > 0.5 ? 1 : -1),
                    rotSpeedZ: horizontalOnly ? 0 : randomBetween(-0.01, 0.01),
                };
            });

            let elapsed = 0;
            let rafId;
            const animate = () => {
                elapsed += 1;
                for (const p of petals) {
                    p.obj.position.y -= p.fallSpeed;
                    p.obj.position.x += Math.sin(elapsed * 0.02 * p.swaySpeed + p.swayOffset) * 0.01 * p.swayAmplitude + wind * 0.01;
                    p.obj.rotation.x += p.rotSpeedX;
                    p.obj.rotation.y += p.rotSpeedY;
                    p.obj.rotation.z += p.rotSpeedZ;

                    if (p.obj.position.y < -worldHeight / 2 - 1) {
                        p.obj.position.y = worldHeight / 2 + 1;
                        p.obj.position.x = randomBetween(-worldWidth / 2, worldWidth / 2);
                    }
                    if (p.obj.position.x > worldWidth / 2 + 1) p.obj.position.x = -worldWidth / 2 - 1;
                    if (p.obj.position.x < -worldWidth / 2 - 1) p.obj.position.x = worldWidth / 2 + 1;
                }
                renderer.render(scene, camera);
                rafId = requestAnimationFrame(animate);
            };
            animate();

            const handleResize = () => {
                const w = mount.clientWidth;
                const h = mount.clientHeight;
                camera.aspect = w / h;
                camera.updateProjectionMatrix();
                renderer.setSize(w, h);
            };
            window.addEventListener('resize', handleResize);

            cleanup = () => {
                cancelAnimationFrame(rafId);
                window.removeEventListener('resize', handleResize);
                geometryPool.forEach((g) => g.dispose());
                material.dispose();
                texture.dispose();
                renderer.dispose();
                if (mount.contains(renderer.domElement)) {
                    mount.removeChild(renderer.domElement);
                }
            };
        });

        return () => {
            cancelled = true;
            cleanup();
        };
    }, [type, count, color, minSize, maxSize, wind, speed]);

    return (
        <div
            ref={mountRef}
            className={className}
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5,
                overflow: 'hidden',
            }}
        />
    );
};

FallingPetalsThree.propTypes = {
    type: PropTypes.oneOf(['lotus', 'bodhi']),
    count: PropTypes.number,
    color: PropTypes.string,
    minSize: PropTypes.number,
    maxSize: PropTypes.number,
    wind: PropTypes.number,
    speed: PropTypes.number,
    className: PropTypes.string,
};

export default FallingPetalsThree;
